import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical, ArrowUp, ArrowDown, Eye, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/admin/AdminUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const FIELD_TYPES = ["text", "email", "textarea", "tel", "number", "select"];
const slugName = (label) => (label || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "champ";

export default function FormsAdmin() {
  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subsFor, setSubsFor] = useState(null);
  const [subs, setSubs] = useState([]);

  const load = () => api.get("/admin/forms").then(({ data }) => setForms(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ name: "", description: "", destination: "crm", submit_label: "Envoyer", active: true, fields: [] });
  const save = async () => {
    if (!editing.name) { toast.error("Nom requis"); return; }
    try {
      if (editing.id) await api.put(`/admin/forms/${editing.id}`, editing);
      else await api.post("/admin/forms", editing);
      toast.success("Formulaire enregistré"); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/admin/forms/${id}`); load(); };
  const openSubs = async (f) => { setSubsFor(f); const { data } = await api.get(`/admin/form_submissions?form_id=${f.id}`); setSubs(data); };

  // field helpers
  const addField = () => setEditing((e) => ({ ...e, fields: [...e.fields, { name: `champ_${e.fields.length + 1}`, label: "Nouveau champ", type: "text", required: false }] }));
  const updField = (i, patch) => setEditing((e) => { const fields = [...e.fields]; fields[i] = { ...fields[i], ...patch }; return { ...e, fields }; });
  const delField = (i) => setEditing((e) => ({ ...e, fields: e.fields.filter((_, k) => k !== i) }));
  const move = (i, dir) => setEditing((e) => { const fields = [...e.fields]; const j = i + dir; if (j < 0 || j >= fields.length) return e; [fields[i], fields[j]] = [fields[j], fields[i]]; return { ...e, fields }; });

  return (
    <div>
      <PageHeader title="Formulaires" subtitle="Créez et personnalisez vos formulaires (champs, ordre, destination)."
        action={<button onClick={openNew} data-testid="form-add-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold"><Plus className="w-4 h-4" /> Nouveau formulaire</button>} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {forms.length === 0 && <Card className="col-span-full"><EmptyState text="Aucun formulaire." /></Card>}
        {forms.map((f) => (
          <Card key={f.id} className="p-5">
            <div className="flex items-start justify-between">
              <div><h3 className="font-semibold text-white">{f.name}</h3><div className="text-xs text-slate-500 mt-0.5">/{f.slug} · {f.fields?.length || 0} champs · {f.destination}</div></div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${f.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{f.active ? "Actif" : "Inactif"}</span>
            </div>
            {f.description && <p className="text-sm text-slate-400 mt-2 line-clamp-2">{f.description}</p>}
            <div className="mt-4 flex items-center gap-1">
              <button onClick={() => setEditing(f)} data-testid={`form-edit-${f.id}`} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1E293B]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => openSubs(f)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1E293B]"><Eye className="w-4 h-4" /></button>
              <a href={`/formulaire/${f.slug}`} target="_blank" rel="noopener" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1E293B]"><ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => remove(f.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-[#1E293B] ml-auto"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>

      {/* Builder dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B] max-w-2xl max-h-[90vh] overflow-y-auto">
          {editing && (<>
            <DialogHeader><DialogTitle className="text-white">{editing.id ? "Modifier" : "Nouveau"} formulaire</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm text-slate-300">Nom</label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" /></div>
                <div><label className="text-sm text-slate-300">Destination des données</label>
                  <select value={editing.destination} onChange={(e) => setEditing({ ...editing, destination: e.target.value })} className="w-full mt-1.5 px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-white text-sm">
                    <option value="crm">CRM (créer un contact)</option><option value="store">Stockage seul</option>
                  </select>
                </div>
              </div>
              <div><label className="text-sm text-slate-300">Description</label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm text-slate-300">Libellé du bouton</label><Input value={editing.submit_label || ""} onChange={(e) => setEditing({ ...editing, submit_label: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" /></div>
                <label className="flex items-center gap-2 text-sm text-slate-300 mt-7"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Formulaire actif</label>
              </div>

              <div className="pt-2 border-t border-[#1E293B]">
                <div className="flex items-center justify-between mb-3"><span className="text-sm font-semibold text-white">Champs</span>
                  <button onClick={addField} data-testid="form-add-field" className="inline-flex items-center gap-1.5 text-xs text-[#4CB944] font-medium"><Plus className="w-3.5 h-3.5" /> Ajouter un champ</button></div>
                <div className="space-y-2">
                  {editing.fields.map((fl, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#0B0F17] border border-[#1E293B]">
                      <GripVertical className="w-4 h-4 text-slate-600 shrink-0" />
                      <Input value={fl.label} onChange={(e) => updField(i, { label: e.target.value, name: slugName(e.target.value) })} placeholder="Libellé" className="bg-[#111827] border-[#1E293B] text-white h-9 text-sm" />
                      <select value={fl.type} onChange={(e) => updField(i, { type: e.target.value })} className="px-2 py-2 rounded-lg bg-[#111827] border border-[#1E293B] text-white text-xs shrink-0">
                        {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-xs text-slate-400 shrink-0"><input type="checkbox" checked={!!fl.required} onChange={(e) => updField(i, { required: e.target.checked })} /> Obl.</label>
                      <button onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-white"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-white"><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => delField(i)} className="p-1 text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  {editing.fields.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">Aucun champ. Ajoutez-en un.</div>}
                </div>
              </div>
            </div>
            <DialogFooter><button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-xl border border-[#1E293B] text-slate-300 text-sm">Annuler</button><button onClick={save} data-testid="form-save-button" className="px-5 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold">Enregistrer</button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Submissions dialog */}
      <Dialog open={!!subsFor} onOpenChange={(o) => !o && setSubsFor(null)}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B] max-w-2xl max-h-[90vh] overflow-y-auto">
          {subsFor && (<>
            <DialogHeader><DialogTitle className="text-white">Soumissions · {subsFor.name}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {subs.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Aucune soumission.</div>}
              {subs.map((s) => (
                <div key={s.id} className="p-3 rounded-lg bg-[#0B0F17] border border-[#1E293B]">
                  <div className="text-xs text-slate-500 mb-1">{new Date(s.created_at).toLocaleString("fr-FR")}</div>
                  {Object.entries(s.data || {}).map(([k, v]) => <div key={k} className="text-sm text-slate-300"><span className="text-slate-500">{k}: </span>{String(v)}</div>)}
                </div>
              ))}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
